import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Alert, AlertDescription } from '../ui/alert';
import { Progress } from '../ui/progress';
import { apiService } from '../../services/api';
import { useToast } from '../ui/use-toast';
import { 
  Calendar, 
  Loader2, 
  AlertCircle, 
  CheckCircle2,
  Clock,
  Users,
  TrendingUp
} from 'lucide-react';
import { format, subDays } from 'date-fns';
import { ro } from 'date-fns/locale';

interface ExtractOvertimeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onExtractionComplete: () => void;
}

export const ExtractOvertimeDialog: React.FC<ExtractOvertimeDialogProps> = ({
  isOpen,
  onClose,
  onExtractionComplete,
}) => {
  const [startDate, setStartDate] = useState(
    format(subDays(new Date(), 7), 'yyyy-MM-dd')
  );
  const [endDate, setEndDate] = useState(
    format(subDays(new Date(), 1), 'yyyy-MM-dd')
  );
  const [skipExisting, setSkipExisting] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<any>(null);
  const { toast } = useToast();

  const handleExtraction = async () => {
    if (!startDate || !endDate) {
      toast({
        title: 'Eroare',
        description: 'Selectați ambele date pentru extragere',
        variant: 'destructive',
      });
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      toast({
        title: 'Eroare',
        description: 'Data de început trebuie să fie înainte de data de sfârșit',
        variant: 'destructive',
      });
      return;
    }

    const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    if (daysDiff > 31) {
      toast({
        title: 'Eroare',
        description: 'Intervalul nu poate depăși 31 de zile',
        variant: 'destructive',
      });
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setResults(null);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 10, 90));
      }, 500);

      const result = await apiService.calculateOvertimeRange({
        startDate: start,
        endDate: end,
        skipExisting,
      });

      clearInterval(progressInterval);
      setProgress(100);
      setResults(result);

      toast({
        title: 'Extragere completă',
        description: `S-au procesat ${result.totalRecordsProcessed} înregistrări și s-au creat ${result.newOvertimeRecordsCreated} înregistrări noi de ore suplimentare`,
      });

      // Refresh the parent component after a delay
      setTimeout(() => {
        onExtractionComplete();
      }, 1500);
    } catch (error) {
      console.error('Error extracting overtime:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut extrage orele suplimentare',
        variant: 'destructive',
      });
      setProgress(0);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setResults(null);
      setProgress(0);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Extrage Ore Suplimentare Istorice
          </DialogTitle>
          <DialogDescription>
            Calculează și extrage orele suplimentare din zilele anterioare
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!results ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startDate">Data început</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={format(subDays(new Date(), 1), 'yyyy-MM-dd')}
                    disabled={isProcessing}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Prima zi din interval
                  </p>
                </div>
                <div>
                  <Label htmlFor="endDate">Data sfârșit</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    max={format(new Date(), 'yyyy-MM-dd')}
                    disabled={isProcessing}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Ultima zi din interval
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="skipExisting"
                  checked={skipExisting}
                  onCheckedChange={(checked) => setSkipExisting(!!checked)}
                  disabled={isProcessing}
                />
                <Label
                  htmlFor="skipExisting"
                  className="text-sm font-normal cursor-pointer"
                >
                  Omite înregistrările existente (nu actualiza orele deja calculate)
                </Label>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Notă:</strong> Procesul va calcula orele suplimentare pentru toate zilele 
                  din intervalul selectat. Pentru fiecare zi, se vor verifica toate intrările de lucru 
                  finalizate și se vor crea înregistrări de ore suplimentare unde este necesar.
                </AlertDescription>
              </Alert>

              {isProcessing && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Procesare în curs...</span>
                    <span>{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4">
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Extragerea s-a finalizat cu succes!
                </AlertDescription>
              </Alert>

              <div className="grid grid-cols-3 gap-4">
                <Card className="p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="h-4 w-4 text-blue-600" />
                    <span className="text-sm text-gray-600">Zile procesate</span>
                  </div>
                  <p className="text-xl font-bold">{results.daysProcessed}</p>
                </Card>

                <Card className="p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="h-4 w-4 text-purple-600" />
                    <span className="text-sm text-gray-600">Total înregistrări</span>
                  </div>
                  <p className="text-xl font-bold">{results.totalRecordsProcessed}</p>
                </Card>

                <Card className="p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="h-4 w-4 text-orange-600" />
                    <span className="text-sm text-gray-600">Ore suplimentare noi</span>
                  </div>
                  <p className="text-xl font-bold text-orange-600">
                    {results.newOvertimeRecordsCreated}
                  </p>
                </Card>
              </div>

              {results.dailyBreakdown && results.dailyBreakdown.length > 0 && (
                <div>
                  <Label className="text-sm mb-2 block">Detalii pe zile:</Label>
                  <div className="max-h-[200px] overflow-y-auto border rounded p-2 space-y-1">
                    {results.dailyBreakdown.map((day: any) => (
                      <div
                        key={day.date}
                        className="flex items-center justify-between text-sm py-1 border-b last:border-0"
                      >
                        <span className="font-medium">
                          {format(new Date(day.date + 'T00:00:00'), 'dd MMM yyyy', { locale: ro })}
                        </span>
                        <div className="flex items-center gap-4">
                          <span className="text-gray-600">
                            {day.recordsProcessed} muncitori
                          </span>
                          {day.totalOvertimeHours > 0 && (
                            <Badge variant="destructive" className="text-xs">
                              {day.totalOvertimeHours.toFixed(1)}h overtime
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {!results ? (
            <>
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isProcessing}
              >
                Anulează
              </Button>
              <Button
                onClick={handleExtraction}
                disabled={isProcessing || !startDate || !endDate}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Procesare...
                  </>
                ) : (
                  <>
                    <Clock className="h-4 w-4 mr-2" />
                    Extrage Ore Suplimentare
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Închide
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Helper Card component for results display
const Card: React.FC<{ className?: string; children: React.ReactNode }> = ({ 
  className = '', 
  children 
}) => (
  <div className={`border rounded-lg ${className}`}>
    {children}
  </div>
);

// Helper Badge component
const Badge: React.FC<{ 
  variant?: 'default' | 'destructive'; 
  className?: string; 
  children: React.ReactNode 
}> = ({ 
  variant = 'default', 
  className = '', 
  children 
}) => {
  const variantClasses = variant === 'destructive' 
    ? 'bg-red-100 text-red-800' 
    : 'bg-gray-100 text-gray-800';
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${variantClasses} ${className}`}>
      {children}
    </span>
  );
};